import React, { useState, useRef, useEffect } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import './NavSubmenu.css';

interface NavSubmenuItem {
    to: string;
    label: string;
    onClick?: () => void;
}

interface NavSubmenuProps {
    label: string;
    items: NavSubmenuItem[];
}

const NavSubmenu: React.FC<NavSubmenuProps> = ({ label, items }) => {
    const [isOpen, setIsOpen] = useState(false);
    const menuRef = useRef<HTMLDivElement>(null);
    const location = useLocation();

    // Check if any item in the submenu is currently active
    const isActiveSubmenu = items.some(item => location.pathname === item.to);

    // Close menu when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Close menu on route change
    useEffect(() => {
        setIsOpen(false);
    }, [location.pathname]);

    return (
        <div className="nav-submenu" ref={menuRef}>
            <button
                type="button"
                className={`nav-submenu-trigger ${isActiveSubmenu ? 'active' : ''}`}
                onClick={() => setIsOpen(!isOpen)}
                aria-expanded={isOpen}
                aria-haspopup="true"
            >
                <span>{label}</span>
                <span className={`nav-submenu-arrow ${isOpen ? 'open' : ''}`}>&#8250;</span>
            </button>
            {isOpen && (
                <div className="nav-submenu-dropdown">
                    {items.map((item) => (
                        <NavLink
                            key={item.to}
                            to={item.to}
                            className={({ isActive }) => `nav-submenu-item ${isActive ? 'active' : ''}`}
                            onClick={() => {
                                item.onClick?.();
                                setIsOpen(false);
                            }}
                        >
                            {item.label}
                        </NavLink>
                    ))}
                </div>
            )}
        </div>
    );
};

export default NavSubmenu;
